import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	inject,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ChangeDetectionStrategy,
} from '@angular/core';
import { DocusignService } from '@lmc-app/core/services/docusign.service';
import { highlightPDFText } from '../../helper-functions/pdf-text-highlight';
import { ListenerService } from '@lmc-app/core/services/listener.service';
import { MatDialog } from '@angular/material/dialog';
import { DocusignGuideComponent } from '../../docusign-guide/docusign-guide.component';
import {
	catchError,
	distinctUntilChanged,
	forkJoin,
	fromEvent,
	map,
	Observable,
	of,
	Subscription,
	switchMap,
	timer,
} from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { NgClass, NgIf, UpperCasePipe } from '@angular/common';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { AgreementsRecipientStatusComponent } from '../agreements-recipient-status/agreements-recipient-status.component';
import { AgreementContentUpdateComponent } from '../agreement-content-update/agreement-content-update.component';

@Component({
	selector: 'app-agreement-preview-modal',
	templateUrl: './agreement-preview-modal.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./agreement-preview-modal.component.scss'],
	imports: [
		MatIcon,
		NgClass,
		NgIf,
		NgxExtendedPdfViewerModule,
		MatProgressSpinner,
		MatTooltip,
		AgreementsRecipientStatusComponent,
		AgreementContentUpdateComponent,
		UpperCasePipe,
	],
})
export class AgreementPreviewModalComponent
	implements OnChanges, OnInit, AfterViewInit, OnDestroy
{
	@Input() selectedRowData: any;
	@Output() onCloseEnvelope = new EventEmitter<boolean>();
	@Output() applicationChange = new EventEmitter();
	pdfSource = null;
	isPdfLoaded = false; // Set by the viewer's pdfLoaded event once the PDF has fully rendered
	loaderText = 'Loading agreement...'; // PDF pane loader message: Deleting -> Creating -> Updating -> Loading
	// ms = how long each message stays before the next one; the last one stays until the PDF arrives
	private readonly envelopeUpdateStages = [
		{ text: 'Deleting agreement...', ms: 2000 },
		{ text: 'Creating agreement...', ms: 2000 },
		{ text: 'Updating agreement...', ms: 5000 },
		{ text: 'Loading agreement...', ms: 0 },
	];
	private readonly minLoadingMs = 2000; // Keep "Loading agreement..." visible at least this long
	private loaderStageTimer?: Subscription;
	dataModel = {};
	isGuideOpen: boolean = false;
	isDraft: boolean = false;
	showRecipients: boolean = false;
	statusTone: 'draft' | 'sent' | 'void' | 'completed' = 'draft';
	readonly editingStatuses = ['created'];
	readonly updatableStatuses = ['created', 'draft'];
	readonly statusToneMap: Record<
		string,
		'draft' | 'sent' | 'void' | 'completed'
	> = {
		created: 'draft',
		completed: 'completed',
		voided: 'void',
	};

	private subs = new Subscription();

	constructor(
		private _docusignService: DocusignService,
		private _listenerService: ListenerService,
		private _docusignGuide: MatDialog
	) {}

	ngOnInit(): void {
		this.subs.add(
			this._listenerService
				.on<boolean>('aggreementStaleUpdate')
				.subscribe((refresh) => {
					if (refresh) {
						this.loadEnvelopePdf(this.selectedRowData?.envelope_id);
					}
				})
		);
		this.subs.add(
			this._listenerService
				.on<string>('aggreementNewUpdate')
				.subscribe((envelopeId) => {
					if (envelopeId) {
						this.selectedRowData.envelope_id = envelopeId;
						this.loadEnvelopePdf(envelopeId);
					}
				})
		);

		this.subs.add(
			this._listenerService
				.on<boolean>('agreementPreviewLoading')
				.subscribe((loading) => {
					if (loading) {
						this.pdfSource = null;
						this.startLoaderStages(this.envelopeUpdateStages);
					}
				})
		);

		this.subs.add(
			this._listenerService
				.on<string>('agreementIsSent')
				.subscribe((envelopeId) => {
					if (envelopeId) {
						this.selectedRowData.envelope_id = envelopeId;
						this.isDraft = false;
						this.showRecipients = false;
						this.statusTone = 'sent';
					}
				})
		);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['selectedRowData']) {
			const statusName =
				this.selectedRowData?.['rowData']?.['statusId']?.[
					'status_name'
				]?.toLowerCase();
			this.isDraft = this.editingStatuses.includes(statusName) ? true : false;
			this.showRecipients = false;
			this.statusTone = this.statusToneMap[statusName] ?? 'sent';
			this.normalizedAgreementData(
				this.selectedRowData?.application,
				this.selectedRowData?.currentUser
			);
			// this.loadEnvelopePdf(this.selectedRowData?.envelope_id)
		}
	}

	updateAgreementDetails(dataModel: any): Observable<any> {
		return this._docusignService.updateAgreementDetails(
			dataModel,
			this.selectedRowData?.currentUser
		);
	}

	// Envelope update deletes, recreates and updates on the backend in one request, so step the loader text while it runs
	private startLoaderStages(stages: { text: string; ms: number }[]): void {
		this.loaderStageTimer?.unsubscribe();
		this.loaderStageTimer = new Subscription();
		let offset = 0;
		stages.forEach(({ text, ms }) => {
			this.loaderStageTimer.add(
				timer(offset).subscribe(() => (this.loaderText = text))
			);
			offset += ms;
		});
	}

	loadEnvelopePdf(envelopeId: string) {
		this.pdfSource = null;
		this.isPdfLoaded = false;
		this.loaderStageTimer?.unsubscribe();
		this.loaderText = 'Loading agreement...';
		forkJoin([
			this._docusignService.streamEnvelope(envelopeId),
			timer(this.minLoadingMs),
		]).subscribe({
			next: ([blob]: [Blob, number]) => {
				const reader = new FileReader();
				reader.onload = () => {
					this.pdfSource = blob;
				};
				reader.readAsArrayBuffer(blob);
			},
			error: (err) => console.error('PDF Load Error:', err),
		});
	}

	normalizedAgreementData(application: any, currentUser: any) {
		this.pdfSource = null;
		const statusName =
			this.selectedRowData?.['rowData']?.['statusId']?.[
				'status_name'
			]?.toLowerCase();
		const isUpdatable = this.updatableStatuses.includes(statusName);
		this.isUpdatingEnvelope = isUpdatable;
		if (isUpdatable) {
			this.startLoaderStages(this.envelopeUpdateStages);
		} else {
			this.loaderText = 'Loading agreement...';
		}
		// The list's application can be stale if it was edited elsewhere, so get it straight from Zoho (falls back to the passed one)
		this._docusignService
			.getUpdatedZohoApplication(application?.id)
			.pipe(
				map((res) => res?.data?.[0] ?? application),
				catchError(() => of(application)),
				switchMap((latestApplication) => {
					if (this.selectedRowData) {
						this.selectedRowData.application = latestApplication;
					}
					return this._docusignService.normalizeAgreementData(
						latestApplication,
						currentUser
					);
				}),
				switchMap((res) => {
					this.dataModel = {
						...res?.[0],
						envelopeId: this.selectedRowData?.envelope_id,
					};
					// Only envelopes still being edited can be updated; others just load the existing PDF
					return isUpdatable
						? this.updateAgreementDetails(this.dataModel)
						: of(null);
				})
			)
			.subscribe({
				next: (res) => {
					// A template diff recreates the envelope, so prefer the new id when one is returned
					const envelopeId =
						res?.jsonData?.docusign_res?.envelopeId ??
						this.selectedRowData?.envelope_id;
					if (this.selectedRowData) {
						this.selectedRowData.envelope_id = envelopeId;
					}
					this.dataModel = { ...this.dataModel, envelopeId };
					this.isUpdatingEnvelope = false;
					this.loadEnvelopePdf(envelopeId);
				},
				error: (err) => {
					console.error('Agreement update error:', err);
					this.isUpdatingEnvelope = false;
					this.loadEnvelopePdf(this.selectedRowData?.envelope_id);
				},
			});
	}

	onTextLayerRendered(event: any): void {
		const containerDiv = event.source?.div || event.element;
		if (!containerDiv || !this.dataModel) return;

		// Map your dictionary's data values into a simple array of strings
		const keywordsToMatch = Object.values(this.dataModel).map((item) =>
			String(item['value'])
		);

		// Execute the reusable utility
		highlightPDFText({
			container: containerDiv,
			keywords: keywordsToMatch,
			className: 'custom-bold-highlight',
		});
	}

	onBackToList() {
		this.onCloseEnvelope.emit(true);
	}

	onRefresh() {
		// Uncomment if pdf needs to reload onRefresh
		// // this.loadEnvelopePdf(this.selectedRowData?.envelope_id)
		this._listenerService.emit('recipientStatus', true);
	}

	openDocusignGuide() {
		this.isGuideOpen = true;
		const dialogRef = this._docusignGuide.open(DocusignGuideComponent, {
			width: 'calc((85vh - 48px) * 0.85)',
			height: '85vh',
			minWidth: '800px',
			minHeight: '400px',
			maxWidth: '95vw',
			maxHeight: '95vh',
			autoFocus: true,
			panelClass: ['hide-dialog-scrollbar', 'custom-dynamic-modal'],
		});
		dialogRef.afterClosed().subscribe(() => (this.isGuideOpen = false));
	}

	ngOnDestroy(): void {
		this.subs.unsubscribe();
		this.loaderStageTimer?.unsubscribe();
	}

	protected onApplicationChanged(application: any): void {
		this.applicationChange.emit(application);
	}

	// Summary content (.scrollable-content) scrolled: shows the preview header bar shadow, same as the create preview
	isPageScrolled = false;
	private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

	ngAfterViewInit(): void {
		const scrollContainer = this._elementRef.nativeElement.closest(
			'.scrollable-content'
		);
		if (!scrollContainer) {
			return;
		}
		this.subs.add(
			fromEvent(scrollContainer, 'scroll', { passive: true })
				.pipe(
					map(() => scrollContainer.scrollTop > 0),
					distinctUntilChanged()
				)
				.subscribe((isScrolled) => (this.isPageScrolled = isScrolled))
		);
	}

	// True while updateAgreementDetails runs, it can recreate the envelope so the recipient list waits for the final envelope id
	isUpdatingEnvelope = false;
}
